/**
 * Generated by orval v8.20.0.
 * Do not edit manually.
 * Hub API v2.0.0.
 */

import type { HttpContext, HttpEvent, HttpParams } from '@angular/common/http';
import { HttpResponse as AngularHttpResponse, HttpClient, HttpHeaders } from '@angular/common/http';

import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

import type {
  AgencyApplicationsDisable200,
  AgencyApplicationsEnable200,
  AgencyApplicationsList200,
  AgencyApplicationsRequest200,
  AgencyApplicationsRequestBody,
} from '../../models';

interface HttpClientOptions {
  readonly headers?: HttpHeaders | Record<string, string | string[]>;
  readonly context?: HttpContext;
  readonly params?:
    | HttpParams
    | Record<string, string | number | boolean | Array<string | number | boolean>>;
  readonly reportProgress?: boolean;
  readonly withCredentials?: boolean;
  readonly credentials?: RequestCredentials;
  readonly keepalive?: boolean;
  readonly priority?: RequestPriority;
  readonly cache?: RequestCache;
  readonly mode?: RequestMode;
  readonly redirect?: RequestRedirect;
  readonly referrer?: string;
  readonly integrity?: string;
  readonly referrerPolicy?: ReferrerPolicy;
  readonly transferCache?: { includeHeaders?: string[] } | boolean;
  readonly timeout?: number;
}

type HttpClientBodyOptions = HttpClientOptions & {
  readonly observe?: 'body';
};

type HttpClientEventOptions = HttpClientOptions & {
  readonly observe: 'events';
};

type HttpClientResponseOptions = HttpClientOptions & {
  readonly observe: 'response';
};

type HttpClientObserveOptions = HttpClientOptions & {
  readonly observe?: 'body' | 'events' | 'response';
};

@Injectable({ providedIn: 'root' })
export class ApplicationsService {
  private readonly http = inject(HttpClient);
  /**
   * Returns every active application the session consumer could connect to, ordered by name, each with the consumer's `connection` to it where it has requested one. A private application is listed only once the consumer is connected to it, pending included, so the rest are not told it exists. A public client is never listed, as a consumer cannot hold a key against one. The `consumer_key` is the one minted under the consumer's prior plan when the application's developer approved the connection, empty until then. Unpaged, as the screen is a chooser. Managers of the consumer's super agency only: the connections and their keys are the consumer's.<br><br>Replaces `GET /v1.5/agent/access/application/list` of the legacy agent API: `data.*` is flattened, with `text_id`, `label`, `image_url` and `is_private` as `agw_id`, `name`, `logo_url` and `private`; `enabled` and `ndc_token` are `connection` being present and `connection.consumer_key`, beside the connection's `status`; `technical_email` is new; `hub_*`, `webhook_url`, `webhook_events`, `enable_ndc` and `email` are gone; and sub-agency managers, whom the legacy answered with `null`, are refused.
   * @summary List the applications the consumer could connect to
   */
  agencyApplicationsList<TData = AgencyApplicationsList200>(
    options?: HttpClientBodyOptions
  ): Observable<TData>;
  agencyApplicationsList<TData = AgencyApplicationsList200>(
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyApplicationsList<TData = AgencyApplicationsList200>(
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyApplicationsList<TData = AgencyApplicationsList200>(
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/applications`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/applications`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.get<TData>(`${environment.hubApiUrl}/agency/applications`, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
  /**
   * Requests the session consumer's connection to the application with the given `agw_id`, public or private, and returns the connection. It is how an agency asks for a private application it has been given the id of, which the listing does not offer. The connection starts pending, without a key: the key is minted under the consumer's prior plan when the application's developer approves it. An unknown `agw_id`, an inactive application, a public client or our internal bot is not found. An application already requested, whatever has become of the request since, is a conflict. Managers of the consumer's super agency only.<br><br>New: the legacy agent API had no way to request a private application.
   * @summary Request an application by its agw_id
   */
  agencyApplicationsRequest<TData = AgencyApplicationsRequest200>(
    agencyApplicationsRequestBody: AgencyApplicationsRequestBody,
    options?: HttpClientBodyOptions
  ): Observable<TData>;
  agencyApplicationsRequest<TData = AgencyApplicationsRequest200>(
    agencyApplicationsRequestBody: AgencyApplicationsRequestBody,
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyApplicationsRequest<TData = AgencyApplicationsRequest200>(
    agencyApplicationsRequestBody: AgencyApplicationsRequestBody,
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyApplicationsRequest<TData = AgencyApplicationsRequest200>(
    agencyApplicationsRequestBody: AgencyApplicationsRequestBody,
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.post<TData>(
        `${environment.hubApiUrl}/agency/applications/request`,
        agencyApplicationsRequestBody,
        {
          ...(options as Omit<NonNullable<typeof options>, 'observe'>),
          observe: 'events',
        }
      );
    }

    if (options?.observe === 'response') {
      return this.http.post<TData>(
        `${environment.hubApiUrl}/agency/applications/request`,
        agencyApplicationsRequestBody,
        {
          ...(options as Omit<NonNullable<typeof options>, 'observe'>),
          observe: 'response',
        }
      );
    }

    return this.http.post<TData>(
      `${environment.hubApiUrl}/agency/applications/request`,
      agencyApplicationsRequestBody,
      {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'body',
      }
    );
  }
  /**
   * Drops the session consumer's connection to the application and every key minted under it: a withdrawal, not a pause. A pending or rejected request is dropped the same way, which is how an agency withdraws a request. A suspended connection is a conflict: suspension is the developer's to lift and keeps the keys, which work again once the developer resumes it, so dropping it would lose them and the approval for nothing. An application the consumer is not connected to is not found. Managers of the consumer's super agency only.<br><br>Replaces `POST /v1.5/agent/access/application/update` with `status: false` of the legacy agent API: the application is in the path rather than `id` in the body; `400 "status not changed"` is a 404; and sub-agency managers are refused.
   * @summary Withdraw an application
   */
  agencyApplicationsDisable<TData = AgencyApplicationsDisable200>(
    applicationId: string,
    options?: HttpClientBodyOptions
  ): Observable<TData>;
  agencyApplicationsDisable<TData = AgencyApplicationsDisable200>(
    applicationId: string,
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyApplicationsDisable<TData = AgencyApplicationsDisable200>(
    applicationId: string,
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyApplicationsDisable<TData = AgencyApplicationsDisable200>(
    applicationId: string,
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.post<TData>(
        `${environment.hubApiUrl}/agency/applications/${applicationId}/disable`,
        undefined,
        {
          ...(options as Omit<NonNullable<typeof options>, 'observe'>),
          observe: 'events',
        }
      );
    }

    if (options?.observe === 'response') {
      return this.http.post<TData>(
        `${environment.hubApiUrl}/agency/applications/${applicationId}/disable`,
        undefined,
        {
          ...(options as Omit<NonNullable<typeof options>, 'observe'>),
          observe: 'response',
        }
      );
    }

    return this.http.post<TData>(
      `${environment.hubApiUrl}/agency/applications/${applicationId}/disable`,
      undefined,
      {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'body',
      }
    );
  }
  /**
   * Requests the session consumer's connection to an application the listing offers and returns the connection. The connection starts pending, without a key: the key is minted under the consumer's prior plan when the application's developer approves it. Any application the listing does not offer is not found, the same answer as one that does not exist, so a private application cannot be found by guessing ids. An application already requested, whatever has become of the request since, is a conflict rather than a silent success. Managers of the consumer's super agency only.<br><br>Replaces `POST /v1.5/agent/access/application/update` with `status: true` of the legacy agent API: the application is in the path rather than `id` in the body; the connection starts pending rather than active with a key; the reply is the connection; `400 "status not changed"` is a 409; and sub-agency managers are refused.
   * @summary Request an application
   */
  agencyApplicationsEnable<TData = AgencyApplicationsEnable200>(
    applicationId: string,
    options?: HttpClientBodyOptions
  ): Observable<TData>;
  agencyApplicationsEnable<TData = AgencyApplicationsEnable200>(
    applicationId: string,
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyApplicationsEnable<TData = AgencyApplicationsEnable200>(
    applicationId: string,
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyApplicationsEnable<TData = AgencyApplicationsEnable200>(
    applicationId: string,
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.post<TData>(
        `${environment.hubApiUrl}/agency/applications/${applicationId}/enable`,
        undefined,
        {
          ...(options as Omit<NonNullable<typeof options>, 'observe'>),
          observe: 'events',
        }
      );
    }

    if (options?.observe === 'response') {
      return this.http.post<TData>(
        `${environment.hubApiUrl}/agency/applications/${applicationId}/enable`,
        undefined,
        {
          ...(options as Omit<NonNullable<typeof options>, 'observe'>),
          observe: 'response',
        }
      );
    }

    return this.http.post<TData>(
      `${environment.hubApiUrl}/agency/applications/${applicationId}/enable`,
      undefined,
      {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'body',
      }
    );
  }
}
